import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Fab,
  Modal,
  CircularProgress,
  IconButton,
  Paper,
  Divider,
  Stack,
  Container,
  Grid,
  Alert,
  Snackbar,
  Fade,
  // Slide,
  // useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Add as AddIcon,

  Visibility as ViewIcon,
  PlayArrow as ContinueIcon,
  Close as CloseIcon,
  Work as WorkIcon,
  Favorite as LoveIcon,
  LocalHospital as HealthIcon,
  AttachMoney as WealthIcon,
  Group as FamilyIcon,
  Star as StarIcon,
  SmartToy as RobotIcon,
  SearchOff as SearchOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';

import { theme as appTheme, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';

interface Consultation {
  id: string;
  question: string;
  answer: string;
  category: string;
  createdAt: any; // Can be Firestore Timestamp, Date, or date string
}

interface ConsultationHistoryScreenProps {}

const ConsultationHistoryScreen: React.FC<ConsultationHistoryScreenProps> = () => {
  const navigate = useNavigate();
  const { consultationHistory, loading: userLoading, refreshData } = useUser();
  
  const [filteredConsultations, setFilteredConsultations] = useState<Consultation[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy] = useState('newest'); // setSortBy removed as it's not used
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const categories = [
    { key: 'all', label: '全部', icon: <StarIcon /> },
    { key: 'career', label: '事业', icon: <WorkIcon /> },
    { key: 'love', label: '爱情', icon: <LoveIcon /> },
    { key: 'health', label: '健康', icon: <HealthIcon /> },
    { key: 'wealth', label: '财富', icon: <WealthIcon /> },
    { key: 'family', label: '家庭', icon: <FamilyIcon /> },
    { key: 'general', label: '综合', icon: <StarIcon /> },
  ];

  useEffect(() => {
    if (consultationHistory) {
      setLoading(false);
    }
  }, [consultationHistory]);

  useEffect(() => {
    filterAndSortConsultations();
  }, [consultationHistory, searchQuery, selectedCategory, sortBy]);



  const filterAndSortConsultations = () => {
    let filtered = consultationHistory || [];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (consultation) =>
          consultation.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          consultation.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((consultation) => consultation.category === selectedCategory);
    }

    // Sort consultations
    filtered.sort((a, b) => {
      const getTimestamp = (date: any) => {
        try {
          if (date && typeof date.toDate === 'function') {
            return date.toDate().getTime();
          } else if (date && typeof date.seconds === 'number') {
            return date.seconds * 1000;
          } else {
            return new Date(date).getTime();
          }
        } catch (error) {
          console.error('Error parsing date for sorting:', error, date);
          return 0;
        }
      };
      
      switch (sortBy) {
        case 'newest':
          return getTimestamp(b.createdAt) - getTimestamp(a.createdAt);
        case 'oldest':
          return getTimestamp(a.createdAt) - getTimestamp(b.createdAt);
        default:
          return 0;
      }
    });

    setFilteredConsultations(filtered);
  };



  const handleViewConsultation = (consultation: Consultation) => {
    setSelectedConsultation(consultation);
    setShowModal(true);
  };

  const handleContinueConsultation = (consultation: Consultation) => {
    navigate('/consultation', { state: { previousConsultation: consultation } });
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      career: appTheme.colors.primary,
      love: appTheme.colors.accent,
      health: '#4CAF50',
      wealth: '#FF9800',
      family: '#9C27B0',
      general: '#2196F3',
    };
    return colors[category as keyof typeof colors] || appTheme.colors.primary;
  };

  const getCategoryIcon = (category: string) => {
    const iconProps = { fontSize: 'small' as const };
    const icons = {
      career: <WorkIcon {...iconProps} />,
      love: <LoveIcon {...iconProps} />,
      health: <HealthIcon {...iconProps} />,
      wealth: <WealthIcon {...iconProps} />,
      family: <FamilyIcon {...iconProps} />,
      general: <StarIcon {...iconProps} />,
    };
    return icons[category as keyof typeof icons] || <StarIcon {...iconProps} />;
  };

  const formatDate = (date: any) => {
    try {
      let dateObj: Date;
      
      // Handle Firestore Timestamp objects
      if (date && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if (date && typeof date.seconds === 'number') {
        // Handle Firestore Timestamp-like objects
        dateObj = new Date(date.seconds * 1000);
      } else {
        // Handle regular Date objects or date strings
        dateObj = new Date(date);
      }
      
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Invalid date';
      }
      
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(dateObj);
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Invalid date';
    }
  };

  if (loading || userLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: responsiveSpacing.section }}>
      {/* Header */}
      <Box sx={{ mb: responsiveSpacing.section }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: appTheme.colors.primary,
            mb: responsiveSpacing.xs,
            display: 'flex',
            alignItems: 'center',
            gap: responsiveSpacing.xs,
            fontSize: responsiveFontSizes.h3,
          }}
        >
          <HistoryIcon sx={{ fontSize: responsiveFontSizes.h2 }} />
          咨询历史
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: responsiveFontSizes.body1 }}>
          查看您过往的咨询记录
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Paper elevation={2} sx={{ p: responsiveSpacing.component, mb: responsiveSpacing.component }}>
        <Grid container spacing={responsiveSpacing.xs} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="搜索咨询记录..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={responsiveSpacing.xs} flexWrap="wrap">
              {categories.map((category) => (
                <Chip
                  key={category.key}
                  icon={category.icon}
                  label={category.label}
                  onClick={() => setSelectedCategory(category.key)}
                  variant={selectedCategory === category.key ? 'filled' : 'outlined'}
                  color={selectedCategory === category.key ? 'primary' : 'default'}
                  sx={{ mb: responsiveSpacing.xs }}
                />
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Consultations List */}
      {filteredConsultations.length === 0 ? (
        <Paper
          elevation={1}
          sx={{
            p: responsiveSpacing.section,
            textAlign: 'center',
            backgroundColor: alpha(appTheme.colors.primary, 0.02),
          }}
        >
          <SearchOffIcon sx={{ fontSize: responsiveFontSizes.h1, color: 'text.secondary', mb: responsiveSpacing.xs }} />
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: responsiveFontSizes.h4 }}>
            {searchQuery || selectedCategory !== 'all'
              ? '未找到匹配的咨询记录'
              : '您还没有咨询记录'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body2 }}>
            {searchQuery || selectedCategory !== 'all'
              ? '请尝试调整搜索条件或筛选器'
              : '开始您的第一次咨询'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RobotIcon />}
            onClick={() => navigate('/consultation')}
            sx={{
              backgroundColor: appTheme.colors.primary,
              '&:hover': {
                backgroundColor: alpha(appTheme.colors.primary, 0.8),
              },
            }}
          >
            开始新咨询
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={responsiveSpacing.component}>
          {filteredConsultations.map((consultation) => (
            <Grid item xs={12} key={consultation.id}>
              <Fade in timeout={300}>
                <Card
                  elevation={2}
                  sx={{
                    '&:hover': {
                      elevation: 4,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <CardContent sx={{ p: responsiveSpacing.component }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: responsiveSpacing.xs }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveSpacing.xs }}>
                        <Chip
                          icon={getCategoryIcon(consultation.category)}
                          label={consultation.category.charAt(0).toUpperCase() + consultation.category.slice(1)}
                          size="small"
                          sx={{
                            backgroundColor: alpha(getCategoryColor(consultation.category), 0.1),
                            color: getCategoryColor(consultation.category),
                            fontWeight: 'medium',
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: responsiveFontSizes.body2 }}>
                          {formatDate(consultation.createdAt)}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleViewConsultation(consultation)}
                          sx={{ mr: 1 }}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleContinueConsultation(consultation)}
                          sx={{ mr: 1 }}
                        >
                          <ContinueIcon />
                        </IconButton>

                      </Box>
                    </Box>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', fontSize: responsiveFontSizes.h4 }}>
                      {consultation.question}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'auto',
                        lineHeight: 1.5,
                        fontSize: responsiveFontSizes.body2,
                      }}
                    >
                      {consultation.answer}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: responsiveSpacing.component,
          right: responsiveSpacing.component,
          backgroundColor: appTheme.colors.primary,
          '&:hover': {
            backgroundColor: alpha(appTheme.colors.primary, 0.8),
          },
        }}
        onClick={() => navigate('/consultation')}
      >
        <AddIcon />
      </Fab>

      {/* Consultation Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        closeAfterTransition
      >
        <Fade in={showModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: '80%', md: '60%' },
              maxHeight: '80vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'auto',
            }}
          >
            {selectedConsultation && (
              <>
                <Box
                  sx={{
                    p: responsiveSpacing.component,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: responsiveFontSizes.h4 }}>
                      咨询详情
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveSpacing.xs }}>
                      <Chip
                        icon={getCategoryIcon(selectedConsultation.category)}
                        label={selectedConsultation.category.charAt(0).toUpperCase() + selectedConsultation.category.slice(1)}
                        size="small"
                        sx={{
                          backgroundColor: alpha(getCategoryColor(selectedConsultation.category), 0.1),
                          color: getCategoryColor(selectedConsultation.category),
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: responsiveFontSizes.body2 }}>
                        {formatDate(selectedConsultation.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={() => setShowModal(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                
                <Box sx={{ p: responsiveSpacing.component, maxHeight: '60vh', overflow: 'auto' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: appTheme.colors.primary, fontSize: responsiveFontSizes.h4 }}>
                    问题:
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: responsiveFontSizes.body1 }}>
                    {selectedConsultation.question}
                  </Typography>
                  
                  <Divider sx={{ my: responsiveSpacing.xs }} />
                  
                  <Typography variant="h6" gutterBottom sx={{ color: appTheme.colors.primary, fontSize: responsiveFontSizes.h4 }}>
                    回答:
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: responsiveFontSizes.body1 }}>
                    {selectedConsultation.answer}
                  </Typography>
                </Box>
                
                <Box
                  sx={{
                    p: responsiveSpacing.component,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: responsiveSpacing.xs,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setShowModal(false)}
                  >
                    关闭
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ContinueIcon />}
                    onClick={() => {
                      setShowModal(false);
                      handleContinueConsultation(selectedConsultation);
                    }}
                    sx={{
                      backgroundColor: appTheme.colors.primary,
                      '&:hover': {
                        backgroundColor: alpha(appTheme.colors.primary, 0.8),
                      },
                    }}
                  >
                    继续咨询
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>



      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ConsultationHistoryScreen;